import CustomTable from "./Table.js";
import { Tag } from "antd";
import {
    BankOutlined,
    EnvironmentOutlined,
    NumberOutlined,
    HomeOutlined,
    CompassOutlined,
    MailOutlined,
} from "@ant-design/icons";

// Color palette for hotel name avatars
const AVATAR_COLORS = [
    "#4e9af1",
    "#fe8447",
    "#4caf7d",
    "#b06de3",
    "#e05c7a",
    "#f0c040",
    "#36c2cf",
    "#ff7043",
];

function getAvatarColor(str = "") {
    let hash = 0;
    for (let i = 0; i < str.length; i++)
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function HotelAvatar({ name = "" }) {
    const initials = name
        .split(" ")
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase();
    const color = getAvatarColor(name);
    return (
        <div
            style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: `${color}22`,
                border: `1.5px solid ${color}55`,
                color,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: 13,
                flexShrink: 0,
                letterSpacing: 0.5,
            }}
        >
            {initials || "H"}
        </div>
    );
}

const columns = [
    {
        title: (
            <span
                className="flex items-center gap-1 text-xs font-semibold tracking-wider uppercase"
                style={{ color: "var(--color-text-muted)" }}
            >
                <NumberOutlined className="text-xs" /> ID
            </span>
        ),
        dataIndex: "id",
        key: "id",
        sorter: () => {},
        width: 90,
        render: (id) => (
            <span
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "var(--color-primary-light)",
                    border: "1px solid var(--color-border)",
                    borderRadius: 6,
                    padding: "2px 10px",
                    fontFamily: "monospace",
                    fontSize: 13,
                    color: "var(--color-primary)",
                    fontWeight: 600,
                    letterSpacing: 0.5,
                }}
            >
                #{id}
            </span>
        ),
    },
    {
        title: (
            <span
                className="flex items-center gap-1 text-xs font-semibold tracking-wider uppercase"
                style={{ color: "var(--color-text-muted)" }}
            >
                <BankOutlined className="text-xs" /> Hotel Name
            </span>
        ),
        dataIndex: "name",
        key: "name",
        sorter: () => {},
        render: (name) => (
            <div className="flex items-center gap-3">
                <HotelAvatar name={name} />
                <div className="flex flex-col">
                    <span
                        style={{
                            color: "var(--color-text)",
                            fontWeight: 600,
                            fontSize: 14,
                            lineHeight: 1.3,
                        }}
                    >
                        {name}
                    </span>
                    <span
                        style={{
                            color: "var(--color-text-muted)",
                            fontSize: 11,
                        }}
                    >
                        Hotel Property
                    </span>
                </div>
            </div>
        ),
    },
    {
        title: (
            <span
                className="flex items-center gap-1 text-xs font-semibold tracking-wider uppercase"
                style={{ color: "var(--color-text-muted)" }}
            >
                <HomeOutlined className="text-xs" /> Street
            </span>
        ),
        dataIndex: "street",
        key: "street",
        sorter: () => {},
        render: (street) => (
            <span
                style={{ color: "var(--color-text-secondary)", fontSize: 13 }}
            >
                {street || (
                    <span style={{ color: "var(--color-text-muted)" }}>—</span>
                )}
            </span>
        ),
    },
    {
        title: (
            <span
                className="flex items-center gap-1 text-xs font-semibold tracking-wider uppercase"
                style={{ color: "var(--color-text-muted)" }}
            >
                <EnvironmentOutlined className="text-xs" /> City
            </span>
        ),
        dataIndex: "city",
        key: "city",
        sorter: () => {},
        render: (city) => (
            <div className="flex items-center gap-1.5">
                <EnvironmentOutlined
                    style={{ color: "var(--color-warning)", fontSize: 12 }}
                />
                <span
                    style={{
                        color: "var(--color-text-secondary)",
                        fontSize: 13,
                        fontWeight: 500,
                    }}
                >
                    {city || (
                        <span style={{ color: "var(--color-text-muted)" }}>
                            —
                        </span>
                    )}
                </span>
            </div>
        ),
    },
    {
        title: (
            <span
                className="flex items-center gap-1 text-xs font-semibold tracking-wider uppercase"
                style={{ color: "var(--color-text-muted)" }}
            >
                <CompassOutlined className="text-xs" /> State
            </span>
        ),
        dataIndex: "state",
        key: "state",
        sorter: () => {},
        width: 100,
        render: (state) =>
            state ? (
                <Tag
                    style={{
                        background: "var(--color-primary-light)",
                        border: "1px solid var(--color-primary)",
                        color: "var(--color-primary)",
                        borderRadius: 6,
                        fontWeight: 600,
                        fontSize: 12,
                        padding: "1px 10px",
                        letterSpacing: 0.5,
                    }}
                >
                    {state.toUpperCase()}
                </Tag>
            ) : (
                <span style={{ color: "var(--color-text-muted)" }}>—</span>
            ),
    },
    {
        title: (
            <span
                className="flex items-center gap-1 text-xs font-semibold tracking-wider uppercase"
                style={{ color: "var(--color-text-muted)" }}
            >
                <MailOutlined className="text-xs" /> Zipcode
            </span>
        ),
        dataIndex: "zipcode",
        key: "zipcode",
        sorter: () => {},
        width: 110,
        render: (zipcode) => (
            <span
                style={{
                    display: "inline-block",
                    background: "var(--color-bg-control)",
                    border: "1px solid var(--color-border)",
                    borderRadius: 6,
                    padding: "2px 10px",
                    fontFamily: "monospace",
                    fontSize: 13,
                    color: "var(--color-text-secondary)",
                }}
            >
                {zipcode || "—"}
            </span>
        ),
    },
];

function HotelList({
    tableData,
    id,
    noOfrecords,
    handlePageChange,
    currentPage,
    currentPageLimit,
    site = undefined,
}) {
    function handleTableChange(pagination, _, sorter) {
        const { current, pageSize } = pagination;
        const { order, field } = sorter;
        const others = {
            page: current - 1,
            limit: pageSize,
        };
        if (order !== undefined) {
            others.sorting = order === "ascend" ? 1 : 2;
            others.sort_by = field;
        }
        others.site = site;
        handlePageChange({ key: id, ...others });
    }

    return (
        <div className="hotel-list-wrapper">
            <CustomTable
                columns={columns}
                tableData={tableData}
                noOfrecords={noOfrecords}
                handleTableChange={handleTableChange}
                rowKey="id"
                className="hotel-table"
                currentPage={currentPage}
                currentPageLimit={currentPageLimit}
                isScroll
            />
        </div>
    );
}

export default HotelList;
